import { useState, useRef, useEffect } from "react"
import { motion, AnimatePresence, useReducedMotion, useInView } from "framer-motion"
import { ChevronLeft, ChevronRight, ArrowRight, Rocket, Bot, Users } from "lucide-react"

export type ServiceKey = "mvp" | "ai" | "team"

const SERVICES = [
  {
    key: "mvp" as ServiceKey,
    icon: Rocket,
    title: "MVP Development",
    shortTitle: "MVP",
    subtitle: "For non-technical founders",
    description: "Go from idea to live product in 30 days. We handle strategy, design, development, and launch so you can focus on your business.",
    features: [
      "Full product team (strategy, design, engineering)",
      "Fixed 30-day timeline",
      "Production-ready code you own"
    ],
    href: "/services/mvp-development",
    cta: "Learn about MVP Development"
  },
  {
    key: "ai" as ServiceKey,
    icon: Bot,
    title: "AI Workflow Automation",
    shortTitle: "AI",
    subtitle: "For operations leaders and teams",
    description: "Automate repetitive work and 10x your team's output. Custom AI agents, integrations, and workflows built for your specific processes.",
    features: [
      "Custom AI agents and automations",
      "Integrations with your existing tools",
      "Measurable time and cost savings"
    ],
    href: "/services/ai-workflow-automation",
    cta: "Learn about AI Automation"
  },
  {
    key: "team" as ServiceKey,
    icon: Users,
    title: "Dedicated Developers",
    shortTitle: "Team",
    subtitle: "For teams with product backlogs",
    description: "Scale your engineering capacity without the hiring drag. Dedicated senior developers who integrate with your team and ship fast.",
    features: [
      "Senior full-stack developers",
      "Async-first, timezone-flexible",
      "No recruiting or onboarding overhead"
    ],
    href: "/services/dedicated-developers",
    cta: "Learn about Dedicated Developers"
  }
]

interface HeroVelocityMobileProps {
  activeService: ServiceKey | null
  onServiceChange: (service: ServiceKey) => void
}

const PANEL_DURATION = 6000

export function HeroVelocityMobile({ activeService, onServiceChange }: HeroVelocityMobileProps) {
  const prefersReducedMotion = useReducedMotion()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [animationKey, setAnimationKey] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const animationRef = useRef<HTMLDivElement>(null)
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isInView = useInView(animationRef as React.RefObject<Element>, {
    once: false,
    amount: 0.3,
    margin: "0px 0px -100px 0px"
  })

  const shouldAnimate = !prefersReducedMotion && isInView
  const currentService = SERVICES[currentIndex]

  const clearAutoAdvanceTimer = () => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current)
      autoAdvanceTimerRef.current = null
    }
  }

  const startAutoAdvanceTimer = (index: number) => {
    clearAutoAdvanceTimer()
    if (!shouldAnimate || prefersReducedMotion) return
    autoAdvanceTimerRef.current = setTimeout(() => {
      const nextIndex = (index + 1) % SERVICES.length
      setCurrentIndex(nextIndex)
      onServiceChange(SERVICES[nextIndex].key)
      setAnimationKey(prev => prev + 1)
    }, PANEL_DURATION)
  }

  const goToPanel = (index: number, isManual = true) => {
    clearAutoAdvanceTimer()
    setCurrentIndex(index)
    onServiceChange(SERVICES[index].key)
    if (isManual) {
      setAnimationKey(prev => prev + 1)
    }
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      const scrollTop = window.scrollY + rect.top - 16
      window.scrollTo({ top: scrollTop, behavior: "smooth" })
    }
  }

  const goNext = () => goToPanel((currentIndex + 1) % SERVICES.length)
  const goPrev = () => goToPanel((currentIndex - 1 + SERVICES.length) % SERVICES.length)

  useEffect(() => {
    if (shouldAnimate) {
      startAutoAdvanceTimer(currentIndex)
    } else {
      clearAutoAdvanceTimer()
    }
    return () => clearAutoAdvanceTimer()
  }, [currentIndex, shouldAnimate, animationKey])

  useEffect(() => {
    if (activeService) {
      const index = SERVICES.findIndex(s => s.key === activeService)
      if (index !== -1 && index !== currentIndex) {
        goToPanel(index, false)
      }
    }
  }, [activeService])

  const taskPillVariants = (index: number) => ({
    animate: {
      opacity: [0, 0, 1, 1, 1, 1],
      transition: { duration: 5, times: [0, index * 0.12, index * 0.12 + 0.1, 0.7, 0.9, 1], ease: "easeInOut" }
    }
  })

  const robotDotVariants = (index: number) => ({
    animate: {
      opacity: [0.3, 1, 0.3, 1, 0.3],
      scale: [0.8, 1.2, 0.8, 1.2, 0.8],
      transition: { duration: 5, delay: index * 0.2, ease: "easeInOut" }
    }
  })

  const checkmarkVariants = {
    animate: {
      scale: [1, 1.15, 1, 1.15, 1],
      opacity: [0.8, 1, 0.8, 1, 0.8],
      transition: { duration: 5, ease: "easeInOut" }
    }
  }

  const fanoutLineVariants = (index: number) => ({
    animate: {
      strokeDashoffset: [0, -96],
      transition: { duration: 5, delay: index * 0.2, ease: "linear" }
    }
  })

  const commitBarVariants = (index: number) => ({
    animate: {
      scaleY: [0, 1, 1, 1],
      transition: { duration: 4, delay: index * 0.06, times: [0, 0.3, 0.8, 1] }
    }
  })

  const prCardVariants = (index: number) => ({
    animate: {
      opacity: [0, 1, 1, 1],
      transition: { duration: 3, delay: index * 1, times: [0, 0.25, 0.85, 1], ease: "easeInOut" }
    }
  })

  const wireframeVariants = {
    animate: {
      opacity: [0, 0, 1, 1, 0, 0],
      transition: { duration: 6, times: [0, 0.4, 0.45, 0.9, 0.95, 1] }
    }
  }

  const polishedVariants = {
    animate: {
      opacity: [1, 1, 0, 0, 1, 1],
      transition: { duration: 6, times: [0, 0.4, 0.45, 0.9, 0.95, 1] }
    }
  }

  const renderMVPAnimation = () => (
    <svg viewBox="0 0 440 380" className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
      <defs>
        <clipPath id="browserClipMobile">
          <rect x="40" y="68" width="360" height="200" rx="8" />
        </clipPath>
      </defs>
      <rect width="440" height="380" rx="14" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" />
      <g transform="translate(28, 24)">
        <text x="0" y="0" fontSize="10" letterSpacing="0.6" fill="#475569">30 DAYS</text>
        <text x="384" y="0" fontSize="10" letterSpacing="0.6" fill="#475569" textAnchor="end">DAY 24</text>
      </g>
      <g transform="translate(28, 34)">
        {Array.from({ length: 30 }, (_, i) => (
          <rect key={`bg-${i}`} x={i * 13} y="0" width="10" height="6" rx="2" fill="#E2E8F0" />
        ))}
      </g>
      <g transform="translate(28, 34)">
        {Array.from({ length: 30 }, (_, i) => (
          <motion.rect
            key={`fill-${i}`}
            x={i * 13} y="0" width="10" height="6" rx="2" fill="#ed2132"
            initial={{ opacity: 0 }}
            animate={shouldAnimate ? { opacity: [0, 0, 1, 1, 1] } : { opacity: prefersReducedMotion ? 1 : 0 }}
            transition={{ duration: 5.5, times: [0, i / 40, (i + 1) / 40 + 0.02, 0.85, 1], ease: "easeOut" }}
          />
        ))}
      </g>
      <rect x="40" y="68" width="360" height="200" rx="8" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="1.5" />
      <rect x="40" y="68" width="360" height="26" rx="8" fill="#FFFFFF" />
      <rect x="40" y="88" width="360" height="6" fill="#FFFFFF" />
      <line x1="40" y1="94" x2="400" y2="94" stroke="#E2E8F0" strokeWidth="1.5" />
      <circle cx="56" cy="81" r="4" fill="#E2E8F0" />
      <circle cx="70" cy="81" r="4" fill="#E2E8F0" />
      <circle cx="84" cy="81" r="4" fill="#E2E8F0" />
      <rect x="106" y="73" width="200" height="16" rx="4" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="1" />
      <motion.g clipPath="url(#browserClipMobile)" variants={wireframeVariants} animate={shouldAnimate ? "animate" : undefined} initial={{ opacity: 0 }}>
        <rect x="58" y="108" width="20" height="20" rx="3" fill="#E2E8F0" />
        <rect x="88" y="112" width="100" height="6" rx="3" fill="#E2E8F0" />
        <rect x="88" y="124" width="64" height="5" rx="2" fill="#E2E8F0" />
        <rect x="58" y="142" width="200" height="14" rx="3" fill="#E2E8F0" />
        <rect x="58" y="164" width="160" height="10" rx="3" fill="#E2E8F0" />
        <rect x="58" y="182" width="200" height="10" rx="3" fill="#E2E8F0" />
        <rect x="58" y="200" width="120" height="10" rx="3" fill="#E2E8F0" />
        <rect x="58" y="220" width="200" height="40" rx="4" fill="none" stroke="#E2E8F0" strokeWidth="1.5" strokeDasharray="4 4" />
        <rect x="280" y="142" width="100" height="28" rx="6" fill="none" stroke="#E2E8F0" strokeWidth="1.5" strokeDasharray="4 4" />
        <rect x="280" y="178" width="100" height="28" rx="6" fill="none" stroke="#E2E8F0" strokeWidth="1.5" strokeDasharray="4 4" />
        <rect x="280" y="214" width="48" height="40" rx="4" fill="none" stroke="#E2E8F0" strokeWidth="1.5" strokeDasharray="4 4" />
        <rect x="332" y="214" width="48" height="40" rx="4" fill="none" stroke="#E2E8F0" strokeWidth="1.5" strokeDasharray="4 4" />
      </motion.g>
      <motion.g clipPath="url(#browserClipMobile)" variants={polishedVariants} animate={shouldAnimate ? "animate" : undefined} initial={{ opacity: 1 }}>
        <circle cx="68" cy="118" r="10" fill="#FEE2E2" />
        <text x="68" y="122" fontSize="9" fontWeight="700" fill="#ed2132" textAnchor="middle">A</text>
        <rect x="88" y="113" width="72" height="5" rx="2" fill="#0F172A" />
        <rect x="88" y="123" width="50" height="4" rx="2" fill="#E2E8F0" />
        <rect x="58" y="140" width="200" height="110" rx="6" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" />
        <text x="68" y="156" fontSize="8" fontWeight="600" fill="#475569" letterSpacing="0.5">REVENUE</text>
        <polyline points="70,236 90,226 110,230 130,214 150,204 170,196 190,178 210,184 248,166" fill="none" stroke="#ed2132" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx="248" cy="166" r="3" fill="#ed2132" />
        <rect x="280" y="140" width="100" height="28" rx="6" fill="#ed2132" />
        <text x="330" y="158" fontSize="9" fontWeight="600" fill="#FFFFFF" textAnchor="middle">Deploy</text>
        <rect x="280" y="176" width="100" height="28" rx="6" fill="#FFFFFF" stroke="#ed2132" strokeWidth="1.5" />
        <text x="330" y="194" fontSize="9" fontWeight="600" fill="#ed2132" textAnchor="middle">Preview</text>
        <rect x="280" y="212" width="48" height="36" rx="4" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="1" />
        <text x="286" y="226" fontSize="7" fill="#475569">USERS</text>
        <text x="286" y="242" fontSize="10" fontWeight="700" fill="#0F172A">2,431</text>
        <rect x="332" y="212" width="48" height="36" rx="4" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="1" />
        <text x="338" y="226" fontSize="7" fill="#475569">MRR</text>
        <text x="338" y="242" fontSize="10" fontWeight="700" fill="#0F172A">$48k</text>
      </motion.g>
      <g transform="translate(28, 290)">
        <rect x="0" y="0" width="384" height="56" rx="28" fill="#FEE2E2" />
        <circle cx="28" cy="28" r="10" fill="#ed2132" />
        <text x="50" y="38" fontSize="22" fontWeight="600" fill="#ed2132">Shipped in 30 days</text>
      </g>
    </svg>
  )

  const renderAIAnimation = () => (
    <svg viewBox="0 0 440 380" className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
      <rect width="440" height="380" rx="14" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" />
      <g fill="none" stroke="#E2E8F0" strokeWidth="1.5" strokeDasharray="4 4">
        <path d="M 158 55 Q 190 80 220 110" />
        <path d="M 158 95 Q 190 110 220 125" />
        <path d="M 158 135 Q 190 140 220 140" />
      </g>
      <g transform="translate(28, 35)">
        <motion.g variants={taskPillVariants(0)} animate={shouldAnimate ? "animate" : undefined} initial={{ opacity: 0 }}>
          <rect x="0" y="0" width="130" height="30" rx="15" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" />
          <circle cx="16" cy="15" r="5" fill="#22C55E" />
          <text x="28" y="19" fontSize="11" fill="#0F172A">Lead intake</text>
        </motion.g>
        <motion.g transform="translate(0, 40)" variants={taskPillVariants(1)} animate={shouldAnimate ? "animate" : undefined} initial={{ opacity: 0 }}>
          <rect x="0" y="0" width="130" height="30" rx="15" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" />
          <circle cx="16" cy="15" r="5" fill="#F59E0B" />
          <text x="28" y="19" fontSize="11" fill="#0F172A">Invoice</text>
        </motion.g>
        <motion.g transform="translate(0, 80)" variants={taskPillVariants(2)} animate={shouldAnimate ? "animate" : undefined} initial={{ opacity: 0 }}>
          <rect x="0" y="0" width="130" height="30" rx="15" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" />
          <circle cx="16" cy="15" r="5" fill="#3B82F6" />
          <text x="28" y="19" fontSize="11" fill="#0F172A">Email triage</text>
        </motion.g>
      </g>
      <g transform="translate(220, 125)">
        <circle r="42" fill="none" stroke="#FECACA" strokeWidth="1.5" strokeDasharray="4 3" />
        <circle r="36" fill="#FEE2E2" />
        {shouldAnimate && (
          <>
            <motion.circle cx="-30" cy="-30" r="4" fill="#ed2132" variants={robotDotVariants(0)} animate="animate" />
            <motion.circle cx="30" cy="-30" r="4" fill="#ed2132" variants={robotDotVariants(1)} animate="animate" />
            <motion.circle cx="0" cy="42" r="4" fill="#ed2132" variants={robotDotVariants(2)} animate="animate" />
          </>
        )}
        <motion.g transform="translate(28, -42)" variants={checkmarkVariants} animate={shouldAnimate ? "animate" : undefined}>
          <circle r="12" fill="#FFFFFF" stroke="#22C55E" strokeWidth="2" />
          <polyline points="-5,0 -1,4 6,-4" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </motion.g>
        <rect x="-18" y="-22" width="36" height="32" rx="8" fill="#FFFFFF" stroke="#ed2132" strokeWidth="2" />
        <circle cx="-7" cy="-8" r="3.5" fill="#ed2132" />
        <circle cx="7" cy="-8" r="3.5" fill="#ed2132" />
        <line x1="-6" y1="4" x2="6" y2="4" stroke="#ed2132" strokeWidth="2" strokeLinecap="round" />
        <line x1="0" y1="-22" x2="0" y2="-32" stroke="#ed2132" strokeWidth="2" strokeLinecap="round" />
        <circle cx="0" cy="-35" r="4" fill="#ed2132" />
      </g>
      <g fill="none" stroke="#E2E8F0" strokeWidth="1.5">
        <motion.path d="M 262 105 Q 310 70 358 40" strokeDasharray="6 6" variants={fanoutLineVariants(0)} animate={shouldAnimate ? "animate" : undefined} />
        <motion.path d="M 262 115 Q 310 100 358 90" strokeDasharray="6 6" variants={fanoutLineVariants(1)} animate={shouldAnimate ? "animate" : undefined} />
        <motion.path d="M 262 135 Q 310 150 358 160" strokeDasharray="6 6" variants={fanoutLineVariants(2)} animate={shouldAnimate ? "animate" : undefined} />
        <motion.path d="M 262 145 Q 310 180 358 210" strokeDasharray="6 6" variants={fanoutLineVariants(3)} animate={shouldAnimate ? "animate" : undefined} />
      </g>
      <g>
        <g transform="translate(358, 28)"><rect width="70" height="24" rx="12" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" /><text x="35" y="16" fontSize="10" fontWeight="600" fill="#0F172A" textAnchor="middle">Gmail</text></g>
        <g transform="translate(358, 78)"><rect width="70" height="24" rx="12" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" /><text x="35" y="16" fontSize="10" fontWeight="600" fill="#0F172A" textAnchor="middle">Slack</text></g>
        <g transform="translate(358, 148)"><rect width="70" height="24" rx="12" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" /><text x="35" y="16" fontSize="10" fontWeight="600" fill="#0F172A" textAnchor="middle">Notion</text></g>
        <g transform="translate(358, 198)"><rect width="70" height="24" rx="12" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" /><text x="35" y="16" fontSize="10" fontWeight="600" fill="#0F172A" textAnchor="middle">Stripe</text></g>
      </g>
      <g transform="translate(28, 290)">
        <rect x="0" y="0" width="384" height="56" rx="28" fill="#FEE2E2" />
        <circle cx="28" cy="28" r="10" fill="#ed2132" />
        <text x="50" y="38" fontSize="20" fontWeight="600" fill="#ed2132">1,247 tasks automated / wk</text>
      </g>
    </svg>
  )

  const renderTeamAnimation = () => (
    <svg viewBox="0 0 440 380" className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
      <rect width="440" height="380" rx="14" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" />
      <g transform="translate(28, 28)">
        <g><circle cx="20" cy="20" r="20" fill="#FEE2E2" /><text x="20" y="25" fontSize="12" fontWeight="700" fill="#ed2132" textAnchor="middle">JM</text></g>
        <g transform="translate(50,0)"><circle cx="20" cy="20" r="20" fill="#FEE2E2" /><text x="20" y="25" fontSize="12" fontWeight="700" fill="#ed2132" textAnchor="middle">RK</text></g>
        <g transform="translate(100,0)"><circle cx="20" cy="20" r="20" fill="#FEE2E2" /><text x="20" y="25" fontSize="12" fontWeight="700" fill="#ed2132" textAnchor="middle">SP</text></g>
        <g transform="translate(0,50)"><circle cx="20" cy="20" r="20" fill="#FEE2E2" /><text x="20" y="25" fontSize="12" fontWeight="700" fill="#ed2132" textAnchor="middle">AT</text></g>
        <g transform="translate(50,50)"><circle cx="20" cy="20" r="20" fill="#FEE2E2" /><text x="20" y="25" fontSize="12" fontWeight="700" fill="#ed2132" textAnchor="middle">DV</text></g>
        <g transform="translate(100,50)"><circle cx="20" cy="20" r="20" fill="#FEE2E2" /><text x="20" y="25" fontSize="12" fontWeight="700" fill="#ed2132" textAnchor="middle">NB</text></g>
      </g>
      <g transform="translate(28, 156)">
        <text x="0" y="0" fontSize="9" fontWeight="600" fill="#475569" letterSpacing="0.5">COMMITS</text>
        <g transform="translate(0, 12)">
          {[
            { x: 0, h: 20 }, { x: 8, h: 28 }, { x: 16, h: 18 }, { x: 24, h: 32 },
            { x: 32, h: 24 }, { x: 40, h: 36 }, { x: 48, h: 22 }, { x: 56, h: 30 },
            { x: 64, h: 18 }, { x: 72, h: 34 }, { x: 80, h: 26 }, { x: 88, h: 20 },
            { x: 96, h: 38 }, { x: 104, h: 28 }, { x: 112, h: 22 }, { x: 120, h: 32 },
            { x: 128, h: 26 }, { x: 136, h: 40, isHighlight: true },
          ].map((bar, i) => (
            <motion.rect
              key={i}
              x={bar.x} y={40 - bar.h} width="5" height={bar.h} rx="1"
              fill={(bar as { isHighlight?: boolean }).isHighlight ? "#ed2132" : "#CBD5E1"}
              variants={commitBarVariants(i)}
              animate={shouldAnimate ? "animate" : undefined}
              style={{ transformOrigin: `${bar.x + 2.5}px 52px` }}
              initial={{ scaleY: 0 }}
            />
          ))}
        </g>
      </g>
      <g transform="translate(220, 20)">
        <motion.g variants={prCardVariants(0)} animate={shouldAnimate ? "animate" : undefined} initial={{ opacity: 0 }}>
          <rect x="0" y="0" width="200" height="50" rx="8" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="1" />
          <text x="14" y="18" fontSize="9" fontWeight="600" fill="#475569">PR #248</text>
          <text x="14" y="34" fontSize="10" fontWeight="600" fill="#0F172A">feat: payments flow</text>
          <g transform="translate(118, 12)">
            <rect width="72" height="26" rx="13" fill="#ECFDF5" stroke="#A7F3D0" strokeWidth="1" />
            <polyline points="12,13 17,18 26,9" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <text x="32" y="17" fontSize="11" fontWeight="700" fill="#059669">Merged</text>
          </g>
        </motion.g>
        <motion.g transform="translate(0, 58)" variants={prCardVariants(1)} animate={shouldAnimate ? "animate" : undefined} initial={{ opacity: 0 }}>
          <rect x="0" y="0" width="200" height="50" rx="8" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="1" />
          <text x="14" y="18" fontSize="9" fontWeight="600" fill="#475569">PR #251</text>
          <text x="14" y="34" fontSize="10" fontWeight="600" fill="#0F172A">fix: webhook retry</text>
          <g transform="translate(118, 12)">
            <rect width="72" height="26" rx="13" fill="#ECFDF5" stroke="#A7F3D0" strokeWidth="1" />
            <polyline points="12,13 17,18 26,9" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <text x="32" y="17" fontSize="11" fontWeight="700" fill="#059669">Merged</text>
          </g>
        </motion.g>
        <motion.g transform="translate(0, 116)" variants={prCardVariants(2)} animate={shouldAnimate ? "animate" : undefined} initial={{ opacity: 0 }}>
          <rect x="0" y="0" width="200" height="50" rx="8" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="1" />
          <text x="14" y="18" fontSize="9" fontWeight="600" fill="#475569">PR #253</text>
          <text x="14" y="34" fontSize="10" fontWeight="600" fill="#0F172A">feat: auth refresh</text>
          <g transform="translate(118, 12)">
            <rect width="72" height="26" rx="13" fill="#ECFDF5" stroke="#A7F3D0" strokeWidth="1" />
            <polyline points="12,13 17,18 26,9" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <text x="32" y="17" fontSize="11" fontWeight="700" fill="#059669">Merged</text>
          </g>
        </motion.g>
      </g>
      <g transform="translate(28, 290)">
        <rect x="0" y="0" width="384" height="56" rx="28" fill="#FEE2E2" />
        <circle cx="28" cy="28" r="10" fill="#ed2132" />
        <text x="50" y="38" fontSize="22" fontWeight="600" fill="#ed2132">Senior devs, integrated</text>
      </g>
    </svg>
  )

  const animations = { mvp: renderMVPAnimation, ai: renderAIAnimation, team: renderTeamAnimation }
  const Icon = currentService.icon

  return (
    <div ref={containerRef} className="w-full space-y-6">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={goPrev}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors shrink-0"
          aria-label="Previous panel"
        >
          <ChevronLeft className="w-5 h-5 text-slate-600" />
        </button>

        <div className="flex items-center gap-1">
          {SERVICES.map((service, index) => (
            <button
              key={service.key}
              type="button"
              onClick={() => goToPanel(index)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                index === currentIndex
                  ? "bg-[#ed2132] text-white"
                  : "bg-slate-100 text-slate-500 hover:bg-slate-200"
              }`}
            >
              {service.shortTitle}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={goNext}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors shrink-0"
          aria-label="Next panel"
        >
          <ChevronRight className="w-5 h-5 text-slate-600" />
        </button>
      </div>

      <div ref={animationRef as React.RefObject<HTMLDivElement>}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`${currentService.key}-${animationKey}`}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.3, ease: "easeInOut" }}
            className="space-y-6"
          >
            <div className="text-center space-y-2">
              <div className="flex items-center justify-center gap-3">
                <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-[#ed2132]">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900">{currentService.title}</h3>
              </div>
              <p className="text-sm text-[#ed2132] font-medium">{currentService.subtitle}</p>
            </div>

            <div className="rounded-2xl overflow-hidden shadow-sm">
              {animations[currentService.key]()}
            </div>

            <div className="space-y-4 px-2">
              <p className="text-slate-600 text-sm leading-relaxed text-center">
                {currentService.description}
              </p>
              <ul className="space-y-2">
                {currentService.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                    <span className="text-[#ed2132] mt-0.5">•</span>
                    {feature}
                  </li>
                ))}
              </ul>
              <a
                href={currentService.href}
                className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-[#ed2132] text-white rounded-lg font-semibold text-sm hover:bg-[#d91e2e] transition-colors"
              >
                {currentService.cta}
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}
